import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/**
 * Nutravia — actions du panier latéral (boutons de paliers, encart abonnement de chaque ligne).
 *
 * Chaque modification du panier coûte ~0,8 s côté Shopify (Kaching recalcule ses remises).
 * Pour aller vite :
 * - l'état du panier est lu dans le tiroir déjà affiché (JSON data-nv-plans), sans requête ;
 * - l'état final est calculé d'abord, puis seules les lignes qui changent sont envoyées,
 *   la dernière requête renvoyant directement le HTML du panier (sections) ;
 * - « abonné depuis le panier » et la fréquence appliquée sont gardés sur la ligne
 *   (propriétés cachées _nv_sub, _nv_freq), envoyés dans la même requête.
 * Toutes les lignes abonnées reçoivent le même plan Kaching : celui du pack correspondant au
 * total de sachets abonnés (1 à 5+), livré au rythme du pack (N sachets = tous les N mois).
 */
const root = window.Shopify?.routes?.root || '/';
const SOURCE = 'nv-cart';

async function cartRequest(path, body) {
  const response = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Erreur panier');
  return data;
}

const getCart = () => fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
const planIdOf = (item) => item.selling_plan_allocation?.selling_plan?.id ?? null;

function nvProps(props = {}) {
  const out = {};
  for (const [key, value] of Object.entries(props)) if (key.startsWith('_nv') && value) out[key] = value;
  return out;
}

function readConfig(drawer) {
  const el = drawer.querySelector('[data-nv-plans]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

// Lignes du panier, depuis le tiroir affiché ou depuis /cart.js
const linesFromConfig = (config) =>
  config.lines.map((l) => ({ variant: l.variant, handle: l.handle, from: l.plan, quantity: l.quantity, props: nvProps(l.props) }));
const linesFromCart = (cart) =>
  cart.items.map((i) => ({ variant: i.variant_id, handle: i.handle, from: planIdOf(i), quantity: i.quantity, props: nvProps(i.properties) }));

/**
 * Plan d'un pack : livraison au rythme du pack (pack N = tous les N mois).
 * Si ce plan n'existe pas, la fréquence la plus longue disponible pour ce pack.
 */
function planForPack(config, pack) {
  const plans = config.plans.filter((plan) => plan.pack === pack && plan.freq <= pack);
  return plans.find((plan) => plan.freq === pack) || plans.sort((a, b) => b.freq - a.freq)[0];
}

/**
 * Calcule l'état voulu et renvoie les lignes à modifier.
 * ops : [{ variant, from, quantity?, plan? ('sub' | null), cartSub? (bool) }].
 */
function planChanges(current, config, ops = []) {
  const isEligible = (l) => !config.filter || l.handle.includes(config.filter);

  const lines = current.map((l) => ({ ...l, plan: l.from, original: l.quantity, nextProps: { ...l.props } }));

  for (const op of ops) {
    const line = lines.find((l) => l.variant === op.variant && l.from === op.from);
    if (!line) continue;
    if (op.quantity !== undefined) line.quantity = op.quantity;
    if (op.plan !== undefined) line.plan = op.plan;
    if (op.cartSub === true) line.nextProps._nv_sub = 'cart';
    // Shopify ne retire pas les propriétés d'une ligne : elle sera remplacée par une ligne neuve
    if (op.cartSub === false) {
      line.nextProps = {};
      line.replace = true;
    }
  }

  const subs = lines.filter((l) => l.plan !== null && l.quantity > 0 && isEligible(l));
  if (subs.length) {
    const total = subs.reduce((sum, l) => sum + l.quantity, 0);
    const pack = Math.min(total, 5);
    const target = planForPack(config, pack);
    const freq = target ? target.freq : pack;
    for (const l of subs) {
      l.plan = target ? target.id : l.from;
      l.nextProps._nv_freq = String(freq);
    }
  }

  const sameProps = (a, b) => JSON.stringify(nvProps(a)) === JSON.stringify(nvProps(b));
  return lines
    .filter(
    (l) =>
      l.quantity !== l.original ||
      l.plan !== l.from ||
      // Les propriétés ne justifient une requête que si la ligne change déjà, ou à l'abonnement / désabonnement
      (!sameProps(l.nextProps, l.props) && (l.plan !== l.from || ops.some((op) => op.variant === l.variant && op.cartSub !== undefined))),
    )
    // La ligne remplacée passe en dernier (l'ajout ne renvoie pas le panier complet)
    .sort((a, b) => Number(Boolean(a.replace)) - Number(Boolean(b.replace)));
}

async function applyChanges(drawer, changes) {
  const sections = { sections: drawer.dataset.sectionId, sections_url: window.location.pathname };
  let cart = null;
  let lines = null;

  for (const [index, change] of changes.entries()) {
    // Ligne repérée dans le dernier panier connu (les clés et l'ordre peuvent bouger)
    const pool = lines || null;
    let line = pool
      ? pool.findIndex((l) => l.variant === change.variant && l.from === change.from)
      : change.index;
    if (pool && line < 0) line = pool.findIndex((l) => l.variant === change.variant);
    if (line < 0) continue;

    const isLast = index === changes.length - 1;

    // Retour à l'achat unique : on retire la ligne abonnée et on ajoute une ligne neuve sans marqueurs,
    // qui se regroupe avec le même parfum déjà en achat unique.
    if (change.replace) {
      await cartRequest('cart/change.js', { line: line + 1, quantity: 0 });
      const added = await cartRequest('cart/add.js', {
        items: [{ id: change.variant, quantity: change.quantity }],
        ...(isLast ? sections : {}),
      });
      cart = { ...(await getCart()), sections: added.sections };
      lines = linesFromCart(cart);
      continue;
    }

    const body = { line: line + 1, quantity: change.quantity };
    if (change.quantity > 0) {
      body.selling_plan = change.plan;
      body.properties = change.nextProps;
    }
    cart = await cartRequest('cart/change.js', isLast ? { ...body, ...sections } : body);
    lines = linesFromCart(cart);
  }

  return cart;
}

function render(result) {
  if (!result?.sections) return;
  document.dispatchEvent(
    new CartUpdatedEvent({}, { itemCount: result.item_count, source: SOURCE, sections: result.sections }),
  );
}

async function addItem(drawer, variant, trigger) {
  drawer.classList.add('nv-loading');
  trigger?.classList.add('nv-busy');
  try {
    const added = await cartRequest('cart/add.js', {
      items: [{ id: variant, quantity: 1 }],
      sections: drawer.dataset.sectionId,
      sections_url: window.location.pathname,
    });
    render({ ...(await getCart()), sections: added.sections });
  } catch (error) {
    console.error(error);
    document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
  } finally {
    drawer.classList.remove('nv-loading');
    trigger?.classList.remove('nv-busy');
  }
}

async function run(drawer, current, ops, trigger) {
  const config = readConfig(drawer);
  if (!config) return;

  // Position de chaque ligne dans le panier actuel (pour la première requête)
  const changes = planChanges(current, config, ops).map((change) => ({
    ...change,
    index: current.findIndex((l) => l.variant === change.variant && l.from === change.from),
  }));
  if (!changes.length) return false;

  drawer.classList.add('nv-loading');
  trigger?.classList.add('nv-busy');
  try {
    render(await applyChanges(drawer, changes));
    return true;
  } catch (error) {
    console.error(error);
    document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
    return false;
  } finally {
    drawer.classList.remove('nv-loading');
    trigger?.classList.remove('nv-busy');
  }
}

document.addEventListener('click', (event) => {
  const drawer = event.target.closest('cart-drawer');
  if (!drawer) return;
  const config = readConfig(drawer);
  if (!config) return;

  // Suppression d'une ligne abonnée : en une seule requête avec la mise à jour des plans
  const remove = event.target.closest('cart-drawer-item [data-ref="remove-item"]');
  if (remove) {
    const index = Number(remove.closest('cart-drawer-item').dataset.index) - 1;
    const line = config.lines[index];
    if (!line?.plan) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    run(drawer, linesFromConfig(config), [{ variant: line.variant, from: line.plan, quantity: 0 }], remove);
    return;
  }

  const trigger = event.target.closest('[data-nv-action]');
  if (!trigger) return;
  event.preventDefault();

  const { nvAction: action, nvVariant, nvFrom, nvQty, nvPlan } = trigger.dataset;
  const variant = Number(nvVariant);
  const from = nvFrom ? Number(nvFrom) : null;
  const current = linesFromConfig(config);

  switch (action) {
    // Palier : la ligne passe à la quantité qui complète le pack
    case 'qty':
      return run(drawer, current, [{ variant, from, quantity: Number(nvQty) }], trigger);
    // Passer cette ligne en abonnement
    case 'sub':
      return run(drawer, current, [{ variant, from, plan: 'sub', cartSub: true }], trigger);
    // Pack (Duo, Trio) : passer la ligne sur le plan du pack
    case 'packsub':
      return run(drawer, current, [{ variant, from, plan: Number(nvPlan), cartSub: true }], trigger);
    // Upsell : ajouter 1 article
    case 'add':
      return addItem(drawer, variant, trigger);
    // Revenir à l'achat unique pour cette ligne
    case 'unsub':
      return run(drawer, current, [{ variant, from, plan: null, cartSub: false }], trigger);
  }
}, true);

// + / − sur une ligne abonnée : quantité et plan en une seule requête
document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!input.matches?.('cart-drawer-item [data-ref="quantity-selector-input"]')) return;

    const drawer = input.closest('cart-drawer');
    const config = readConfig(drawer);
    const index = Number(input.closest('cart-drawer-item').dataset.index) - 1;
    const line = config?.lines[index];
    if (!line?.plan) return;

    const quantity = Number(input.value);
    if (!quantity) return;

    event.stopImmediatePropagation();
    run(drawer, linesFromConfig(config), [{ variant: line.variant, from: line.plan, quantity }]);
  },
  true,
);

// Remet sur chaque ligne abonnée le plan du pack correspondant au panier réel.
// Renvoie true si le panier a été modifié.
async function syncPlans() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || !readConfig(drawer)) return false;

  try {
    return await run(drawer, linesFromCart(await getCart()), []);
  } catch (error) {
    console.error(error);
    return false;
  }
}

const onCartPage = () => /\/cart\/?$/.test(window.location.pathname);

// Ajout depuis la fiche produit (ou autre changement) : on remet les bons plans si besoin.
document.addEventListener(ThemeEvents.cartUpdated, (event) => {
  if (event.detail?.data?.source === SOURCE) return;
  syncPlans();
});

// Au chargement de chaque page : la page /cart (ouverte notamment depuis le checkout) change les
// quantités en rechargeant la page, sans passer par le tiroir. Une ligne abonnée pouvait donc garder
// le plan d'un palier plus haut (ex. 3 sachets au prix du pack 5). On recale, puis on recharge la
// page panier pour afficher les bons prix.
const syncOnLoad = async () => {
  if (await syncPlans() && onCartPage()) window.location.reload();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncOnLoad);
else syncOnLoad();

// Passage au paiement : on vérifie les plans juste avant de partir au checkout.
document.addEventListener(
  'click',
  async (event) => {
    const button = event.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (!button || button.dataset.nvChecked) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    await syncPlans();
    button.dataset.nvChecked = '1';
    button.click();
  },
  true,
);
